import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createFileRoute,
  useMatches,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { FileText } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";

import { useApi } from "../../api";
import { getArtifact } from "../../api/artifacts";
import { CanvasArtifacts } from "../../components/artifact/CanvasArtifacts";
import { useCanvasArtifactsWithTags } from "../../components/artifact/useCanvasArtifactsWithTags";
import { CanvasViewer } from "../../components/canvas";
import { CanvasCommentsOverlay } from "../../components/canvas/CanvasCommentsOverlay";
import { CanvasHistory } from "../../components/canvas/CanvasHistory";
import { ConversationSwitcher } from "../../components/chat/ConversationSwitcher";
import { canonicalSlug, routeWorkspace } from "../../lib/routeWorkspace";
// Sentinel search value that puts the chat into "draft" mode — no thread
// row exists yet, but the composer is enabled and the first sent message
// triggers thread creation lazily (with the title derived from the
// message). Lets the user click "Start a new conversation" and see the
// empty chat instantly without paying for a round-trip first. It lives with
// the resolver that decides when to navigate to it.
import {
  DRAFT_CONVERSATION,
  resolveConversationTarget,
} from "../../components/chat/conversationTarget";
import { canEdit } from "../../components/chat/railMode";
import { useActiveThread, useChatHeaderSlot } from "../../lib/active-thread";
import { useDocumentTitle } from "@/lib/document-title";

const canvasSearchSchema = z.object({
  conversation: z.string().optional(),
});

export const Route = createFileRoute("/_app/w/$slug/c/$canvasId")({
  validateSearch: canvasSearchSchema,
  component: CanvasPage,
});

function CanvasPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { slug, canvasId } = useParams({ from: "/_app/w/$slug/c/$canvasId" });
  const { conversation: selectedThreadId } = useSearch({ from: "/_app/w/$slug/c/$canvasId" });
  const navigate = useNavigate();

  // TanStack file-based routing nests `/a/$artifactId` under this canvas
  // route. Without an <Outlet/>, the artifact route's component never
  // mounts and the URL silently falls back to the canvas page. Detect
  // the nested-route match and short-circuit to <Outlet/> when the
  // user is browsing an artifact. The canvas route's auto-redirect
  // effects still run (they're benign here — they only touch search
  // params, not the path), so the chat panel stays consistent.
  const matches = useMatches();
  const artifactMatch = matches.find((m) => m.routeId.endsWith("/c/$canvasId/a/$artifactId"));
  const onArtifactRoute = artifactMatch !== undefined;
  // The artifact id of the active artifact subroute (null on the plain
  // canvas page). Read off the child match — this route's own params
  // don't carry it. Used to keep the conversation switcher pointed at
  // the artifact editor after e.g. deleting the pinned conversation.
  const activeArtifactId = artifactMatch
    ? ((artifactMatch.params as unknown as { artifactId?: string }).artifactId ?? null)
    : null;
  const { setActiveThread } = useActiveThread();

  const payloadQuery = useQuery({
    queryKey: ["canvas", canvasId],
    queryFn: () => api.getCanvas(canvasId),
  });

  // A 4xx on the canvas fetch (404 = deleted, 403 = lost access) is a
  // terminal state — surface a clean error and STOP the rest of the
  // page's data fetches. Without this gate, listCanvasThreads /
  // proposalsSummary / the chat panel's agent-events poll would each
  // fire and 404 in parallel, producing a spinner→error→spinner "crash
  // loop" feel even though no individual query was infinite-retrying.
  //
  // ``payloadQuery.error`` is typed ``Error | null`` (react-query). The
  // safer narrowing path is to extract ``status`` explicitly and then
  // treat the rest as a plain Error.
  const canvasError = payloadQuery.error ?? null;
  const canvasStatus =
    canvasError && typeof (canvasError as { status?: unknown }).status === "number"
      ? (canvasError as unknown as { status: number }).status
      : null;
  const canvasMissing = canvasStatus !== null && canvasStatus >= 400 && canvasStatus < 500;
  const canvasReady = !!payloadQuery.data;

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });
  // The canvas is fetched BY ID and is authoritative about which workspace
  // owns it; the slug in the URL is a human-readable label that can be STALE —
  // a renamed workspace, or a link minted before a slug collision appended a
  // suffix (a demo's `landing_path` and first-run hrefs are authored that way,
  // so a tenant whose space landed on `acme-1` still hands out `/w/acme/...`).
  //
  // Match the slug first, then fall back to the canvas's own workspace. Without
  // the fallback `workspace` stays undefined forever and the page half-dies in
  // a way that reads as a bug in something else: the canvas body renders (it
  // only needs `canvasId`), but the draft-mode effect below bails on
  // `!workspace`, so no conversation is ever selected and the chat panel —
  // composer AND conversation switcher — silently never mounts. Filmed as
  // "the switcher is missing threads that exist" and "the prefill went
  // nowhere", neither of which is about threads or prefill.
  const canvasWorkspaceId = payloadQuery.data?.canvas.workspace_id ?? null;
  const workspace = routeWorkspace(workspaces.data, slug, canvasWorkspaceId);

  // Having resolved it, put the real slug in the URL. The page works either
  // way now, but a canonical URL keeps a stale-slug visit and an in-app click
  // on the same canvas from being two different-looking sessions. Skipped on
  // the artifact subroute, where this navigation would leave the artifact
  // (that route canonicalises its own URL the same way).
  const canonical = canonicalSlug(workspace, slug);
  useEffect(() => {
    if (!canonical || onArtifactRoute) return;
    void navigate({
      to: "/w/$slug/c/$canvasId",
      params: { slug: canonical, canvasId },
      search: { conversation: selectedThreadId },
      replace: true,
    });
  }, [canonical, canvasId, navigate, onArtifactRoute, selectedThreadId]);

  // Artifacts (+ their Plan tags) for the desk-header meta line, the derived
  // canvas status pill, and the footer list. Shared cache with CanvasArtifacts.
  const canvasArtifacts = useCanvasArtifactsWithTags(canvasId);

  const threads = useQuery({
    queryKey: ["canvas-threads", canvasId],
    queryFn: () => api.listCanvasThreads(canvasId),
    enabled: !!canvasId && canvasReady,
  });

  const canvasThreads = useMemo(() => threads.data ?? [], [threads.data]);

  // Three URL/state shapes for the chat panel:
  //   - `?conversation=<ulid>` → resolves to that thread (validated below)
  //   - `?conversation=draft`  → draft mode; chat is empty, thread is
  //                              created lazily on the first message
  //   - no param               → pick the user's most recent thread on
  //                              this canvas, or drop into draft mode
  //                              if they have none yet
  //
  // The rules — including the two races they exist for — live in
  // `resolveConversationTarget`, which is pure and has the cases as a table:
  //
  //   1. The lazy draft-promotion flow: the composer creates a thread and
  //      navigates to `?conversation=NEW_ID` while the list refetch that
  //      will contain it is still in flight. Falling back to
  //      `canvasThreads[0]` there stamps the URL back to the PREVIOUS
  //      conversation.
  //   2. The deep-link race: this query is DISABLED until the canvas payload
  //      arrives, and a disabled react-query reports isLoading=false,
  //      isFetching=false and no data. "Not settling" therefore meant "has
  //      settled, and this canvas has no threads", so opening a thread by
  //      URL rewrote it to `?conversation=draft` before the list was even
  //      requested — roughly half the time, depending on whether the canvas
  //      payload was already cached.
  //
  // Hence `threads.isSuccess` and not `!isLoading`: the decision needs
  // evidence that the list HAS loaded, which only a success gives.
  const isDraftMode = selectedThreadId === DRAFT_CONVERSATION;
  const threadIds = useMemo(() => canvasThreads.map((t) => t.id), [canvasThreads]);
  const conversationTarget = resolveConversationTarget({
    selected: selectedThreadId,
    threadIds,
    loaded: threads.isSuccess,
    fetching: threads.isFetching,
  });
  const resolvedThreadId = conversationTarget.resolved;

  // What the switcher should mark "active". On the canvas page it's the
  // resolved canvas thread. On the artifact subroute the chat is bound
  // by the CHILD route (`?conversation=` pin > artifact.chat_thread_id
  // — mirror that priority here so the check mark lands on the row the
  // chat is actually bound to). The ["artifact", id] query key is
  // shared with the artifact route's own fetch, so this subscription
  // adds no extra network.
  const activeArtifactQuery = useQuery({
    queryKey: ["artifact", activeArtifactId],
    queryFn: () => getArtifact(activeArtifactId as string),
    enabled: !!activeArtifactId,
  });
  const artifactBoundThreadId = activeArtifactQuery.data?.artifact?.chat_thread_id ?? null;
  const pinnedArtifactThreadId =
    selectedThreadId && selectedThreadId !== DRAFT_CONVERSATION ? selectedThreadId : null;
  const switcherActiveThreadId = onArtifactRoute
    ? (pinnedArtifactThreadId ?? artifactBoundThreadId)
    : resolvedThreadId;

  // Auto-enter draft mode on first visit if the user has zero threads on
  // this canvas. We deliberately do NOT pre-create a thread — the first
  // sent message does that, with a title derived from what they typed.
  // "Zero threads" is `resolveConversationTarget`'s call, not
  // `canvasThreads.length === 0`: an empty array is also what an unloaded
  // list looks like.
  useEffect(() => {
    if (!workspace) return;
    if (selectedThreadId) return;
    if (onArtifactRoute) return; // Don't redirect on artifact subroute
    if (conversationTarget.redirect !== DRAFT_CONVERSATION) return;
    void navigate({
      to: "/w/$slug/c/$canvasId",
      params: { slug, canvasId },
      search: { conversation: DRAFT_CONVERSATION },
      replace: true,
    });
  }, [
    workspace,
    conversationTarget.redirect,
    selectedThreadId,
    slug,
    canvasId,
    navigate,
    onArtifactRoute,
  ]);

  // If the URL search param doesn't match a real thread (and isn't the
  // draft sentinel), redirect to the resolved one — or, when NOTHING on
  // this canvas resolves at all, drop the param and fall into draft mode.
  //
  // BUG A (frontend/runtime coupling): a `?conversation=<id>` that this
  // canvas's own thread list can never contain (a foreign/stale id — e.g.
  // a bookmarked link, or a Plan/workspace-surface thread id that leaked
  // into this URL) used to leave `resolvedThreadId` permanently `null`
  // while `selectedThreadId` stayed non-empty and non-draft. Neither this
  // effect (gated on `!resolvedThreadId`) nor the auto-draft effect above
  // (gated on `!selectedThreadId`) would fire, so the URL — and the
  // ActiveThread binding below (`draft: isDraftMode` = false) — got stuck.
  // If the rail was visible at that moment (e.g. mid-fetch, trusting the
  // stale id optimistically — see the comment above `resolvedThreadId`),
  // "Start a new conversation" / the composer's lazy-create could then
  // fire against a canvas page with no canvas-scoped thread resolved,
  // which is exactly the shape of a thread created with no canvas_id.
  // Waiting for the list to settle before judging "unresolvable" (rather
  // than reading `resolvedThreadId` directly) also skips this redirect
  // during that same trust window, so it can't fight the optimistic bind.
  //
  // Skip on the artifact subroute — that route owns its own URL and
  // writing the canvas-only path here would silently strip /a/$artifactId
  // from the URL bar (observed 2026-05-18 when navigating to an artifact
  // link from the sidebar landed on the canvas page).
  useEffect(() => {
    if (onArtifactRoute) return;
    if (isDraftMode) return;
    if (!selectedThreadId) return;
    // `redirect` is non-null only once the list has come back AND cannot
    // contain this id: a real dead link, not a link we have yet to check.
    const target = conversationTarget.redirect;
    if (!target || target === selectedThreadId) return;
    void navigate({
      to: "/w/$slug/c/$canvasId",
      params: { slug, canvasId },
      search: { conversation: target },
      replace: true,
    });
  }, [
    isDraftMode,
    conversationTarget.redirect,
    selectedThreadId,
    slug,
    canvasId,
    navigate,
    onArtifactRoute,
  ]);

  // Render ConversationSwitcher in the chat header (when we have a workspace).
  // Memoize the JSX so its referential identity only changes when the
  // underlying data does — otherwise the chat-header-slot publishing loop
  // would churn on every commit (and could in extreme cases bust React's
  // re-render budget; see useChatHeaderSlot).
  const handleConversationSelect = useCallback(
    (threadId: string) => {
      void navigate({
        to: "/w/$slug/c/$canvasId",
        params: { slug, canvasId },
        search: { conversation: threadId },
      });
    },
    [navigate, slug, canvasId],
  );
  const handleStartDraft = useCallback(() => {
    // On the artifact subroute, "New conversation" must start a fresh
    // conversation OVER the artifact — navigating to the canvas draft
    // URL here would silently strip /a/$artifactId and bounce the user
    // out of the editor. The artifact route registers a window hook
    // while mounted (same pattern as __chaiArtifactDraftCreator) that
    // mints a fresh artifact thread and re-binds the chat in place.
    if (onArtifactRoute) {
      const startFresh = (
        window as unknown as {
          __chaiArtifactStartFresh?: () => Promise<void>;
        }
      ).__chaiArtifactStartFresh;
      if (startFresh) {
        void startFresh().catch((err) => {
          toast.error("Couldn't start a new conversation", {
            description: (err as Error).message,
          });
        });
        return;
      }
    }
    void navigate({
      to: "/w/$slug/c/$canvasId",
      params: { slug, canvasId },
      search: { conversation: DRAFT_CONVERSATION },
    });
  }, [navigate, slug, canvasId, onArtifactRoute]);
  const handleConversationDeleted = useCallback(
    (deletedId: string) => {
      // If the user just deleted the conversation they were looking at,
      // bounce to the next-most-recent one or into draft mode. We don't
      // know the new list yet (cache is being invalidated), so we
      // optimistically drop the URL param and let the auto-resolve
      // effects above re-route.
      //
      // We compare against `selectedThreadId` (the URL value, stable
      // through the deletion) rather than `resolvedThreadId` (which
      // collapses to the next-most-recent the moment the cache
      // refetches). Comparing against `resolvedThreadId` was the old
      // bug: by the time the mutation's onSuccess fires, the cache had
      // already been invalidated/refetched, `resolvedThreadId` no
      // longer pointed at the deleted thread, the equality check
      // returned early, and the URL stayed pinned to the now-ghost id.
      if (deletedId !== selectedThreadId) return;
      // On the artifact subroute the selected conversation is the
      // `?conversation=` pin — stay IN the editor and just drop the pin
      // (keeping e.g. `version`) so the artifact's own chat_thread_id
      // re-binds. Navigating to the canvas path here would strip
      // /a/$artifactId and bounce the user out of the artifact editor.
      if (onArtifactRoute && activeArtifactId) {
        void navigate({
          to: "/w/$slug/c/$canvasId/a/$artifactId",
          params: { slug, canvasId, artifactId: activeArtifactId },
          search: (prev) => ({ ...prev, conversation: undefined }),
          replace: true,
        });
        return;
      }
      void navigate({
        to: "/w/$slug/c/$canvasId",
        params: { slug, canvasId },
        search: {},
        replace: true,
      });
    },
    [navigate, slug, canvasId, selectedThreadId, onArtifactRoute, activeArtifactId],
  );
  const handleArtifactThreadSelect = useCallback(
    (threadId: string, artifactId: string) => {
      if (!artifactId) return;
      // Carry the picked thread id into the artifact route as a
      // `?conversation=` pin. The artifact page binds the pin over the
      // artifact's newest `chat_thread_id` — this is what makes OLD
      // artifact conversations reopenable from the switcher (previously
      // the id was dropped here and the page always bound the newest).
      // Passing it for the artifact's CURRENT thread too is harmless:
      // the pin and chat_thread_id then agree.
      void navigate({
        to: "/w/$slug/c/$canvasId/a/$artifactId",
        params: { slug, canvasId, artifactId },
        search: { conversation: threadId },
      });
    },
    [navigate, slug, canvasId],
  );
  const conversationSwitcherSlot = useMemo(
    () =>
      workspace ? (
        <ConversationSwitcher
          workspaceId={workspace.id}
          canvasId={canvasId}
          activeThreadId={switcherActiveThreadId}
          onSelect={handleConversationSelect}
          onSelectArtifactThread={handleArtifactThreadSelect}
          onStartDraft={handleStartDraft}
          onDeleted={handleConversationDeleted}
        />
      ) : null,
    [
      workspace,
      canvasId,
      switcherActiveThreadId,
      handleConversationSelect,
      handleArtifactThreadSelect,
      handleStartDraft,
      handleConversationDeleted,
    ],
  );
  useChatHeaderSlot(conversationSwitcherSlot);

  // After the user sends their first message in draft mode, ChatPanel
  // creates the thread and asks the route to promote the draft URL to
  // the real thread id. This avoids creating a thread on click and lets
  // the title be derived from what the user actually typed.
  const handleDraftPromoted = useCallback(
    (newThreadId: string) => {
      void queryClient.invalidateQueries({ queryKey: ["canvas-threads", canvasId] });
      if (workspace) {
        void queryClient.invalidateQueries({ queryKey: ["threads", workspace.id] });
      }
      void navigate({
        to: "/w/$slug/c/$canvasId",
        params: { slug, canvasId },
        search: { conversation: newThreadId },
        replace: true,
      });
    },
    [queryClient, canvasId, workspace, navigate, slug],
  );

  // Bind the resolved thread for the chat panel. Declared after
  // `handleDraftPromoted` so the callback is in-scope. The chat panel
  // reads this out of context and calls `onDraftPromoted` after it
  // lazily creates a thread on the first sent message.
  //
  // CRITICAL: skip on the artifact subroute. This route stays mounted
  // (it renders <Outlet/> for the artifact), and the artifact route owns
  // the active thread (its own artifact-scoped chat). Without this guard,
  // this parent effect runs AFTER the child's (effects fire child→parent)
  // and clobbers the artifact thread with the canvas thread — so opening
  // an artifact showed the canvas chat (or none) instead of the artifact
  // chat. The early return registers no cleanup, so the child's binding
  // survives.
  useEffect(() => {
    if (onArtifactRoute) return;
    setActiveThread({
      threadId: resolvedThreadId,
      canvasId,
      workspaceId: workspace?.id ?? null,
      draft: isDraftMode,
      onDraftPromoted: handleDraftPromoted,
      // The same "Start a new conversation" the switcher offers — the chat
      // panel opens one for a demo replay that started over a transcript.
      onStartDraft: handleStartDraft,
    });
    return () =>
      setActiveThread({
        threadId: null,
        canvasId: null,
        workspaceId: null,
        draft: false,
      });
  }, [
    onArtifactRoute,
    resolvedThreadId,
    canvasId,
    workspace?.id,
    isDraftMode,
    setActiveThread,
    handleDraftPromoted,
    handleStartDraft,
  ]);

  const setVariable = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.setVariableValue(canvasId, key, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["canvas", canvasId],
        exact: true,
      });
    },
  });

  // The tab names the canvas, then its workspace (lib/document-title). On the
  // artifact subroute the artifact page names its own, and wins: it is deeper.
  useDocumentTitle(
    canvasMissing
      ? [canvasStatus === 403 ? "No access" : "Canvas not found"]
      : [payloadQuery.data?.canvas.name ?? "Canvas", workspace?.name],
  );

  // When the URL is the artifact subroute, hand off to <Outlet/> so the
  // artifact route's component (ArtifactPanel) takes over the main pane.
  // The canvas-loading branch must still get a chance to fall through
  // for the artifact route, since the artifact panel doesn't depend on
  // the canvas payload — render the outlet directly.
  if (onArtifactRoute) {
    return <Outlet />;
  }

  if (payloadQuery.isLoading) {
    return (
      <div className="grid min-h-0 flex-1 place-content-center">
        {/* Spinners are a "the app is working" moment — wear the ochre brand
            so the only time we see the brand color spin is when we're
            actually doing something. */}
        <Spinner className="text-[var(--color-brand-ochre)]" />
      </div>
    );
  }

  if (payloadQuery.isError || !payloadQuery.data) {
    // The 4xx case is the "you landed on a URL whose canvas is gone /
    // not yours" situation — render it as a final state with a way back
    // to the workspace. The 5xx case keeps the destructive alert so the
    // user can see what went wrong. Either way, no auto-retry: the
    // global QueryClient already disables 4xx retries, and we early-
    // return BEFORE ChatPanel / TopBar mount so their polls don't fire.
    if (canvasMissing) {
      const status = canvasStatus ?? 404;
      const workspaceHref = workspace ? `/w/${workspace.slug}` : "/";
      return (
        <div className="mx-auto flex max-w-[640px] flex-col gap-5 px-12 py-16">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--color-steel)]">
              Error {status}
            </p>
            <h1 className="mt-1 text-[28px] font-medium leading-tight text-[var(--color-ink)]">
              {status === 403
                ? "You don't have access to this canvas."
                : "This canvas no longer exists."}
            </h1>
            <p className="mt-2 text-sm text-[var(--color-slate)]">
              {status === 403
                ? "Ask a workspace admin to share it with you, or pick a different canvas from the sidebar."
                : "It may have been deleted, or the link is stale. Head back to the workspace to see what's still here."}
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href={workspaceHref}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-3.5 py-2 text-[13px] font-medium text-[var(--color-on-primary)] transition-colors hover:bg-[var(--color-primary-pressed)]"
            >
              ← Back to {workspace?.name ?? "workspace"}
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-[820px] px-12 py-12">
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the canvas</AlertTitle>
          <AlertDescription>
            {(canvasError as Error | null)?.message ?? "Try refreshing the page."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Per-canvas edit gate. Mirrors the backend WorkspaceRole.EDITOR floor on
  // the canvas's mutating routes — editor+ on THIS canvas's workspace space
  // may edit it, regardless of org-level admin. Replaces the old org-level
  // `isAdmin` gate, which conflated "org owner/admin" with "may edit this
  // canvas". `my_role` rides the canvas payload (Part A).
  const myRole = payloadQuery.data.my_role;
  const mayEdit = canEdit(myRole);

  const canvas = payloadQuery.data.canvas;
  const revisionId = payloadQuery.data.current_revision?.id?.slice(-6).toUpperCase() ?? null;
  const updatedAt = new Date(canvas.updated_at);
  const updatedRel = formatDistanceToNow(updatedAt, { addSuffix: true });
  const pageCount = payloadQuery.data.pages.length;
  // Landscape / Manager-Review canvases are zoned (side-by-side columns) and
  // need more horizontal room than the prose-width default, so the sheet runs
  // wider with trimmed side padding for them.
  const isLandscape = payloadQuery.data.pages.some((p) => {
    const c = (p.config ?? {}) as Record<string, unknown>;
    return (
      c.orientation === "landscape" ||
      ["title", "summary", "divider", "manager_review", "cover"].includes(String(c.template))
    );
  });

  const { artifactCount, taggedCount } = canvasArtifacts;
  const status = deriveCanvasStatus(artifactCount, taggedCount);
  const description = canvasDescription(canvas);

  return (
    // The scroll area is the "desk" — a cool neutral (--color-canvas-desk).
    // The white .canvas-page sheet floats above it. The header (name, status,
    // meta) and the artifacts list live ON the desk, around the sheet — the
    // canvas is sacred (mockup CanvasWork).
    <ScrollArea className="bg-[var(--color-canvas-desk)] min-h-0 flex-1">
      <div
        className={`mx-auto px-6 py-10 sm:px-10 ${isLandscape ? "max-w-[1340px]" : "max-w-[1040px]"}`}
      >
        {/* Desk header — back · CANVAS eyebrow · status + actions · title ·
            description · meta line (mockup CanvasWork toolbar). */}
        <div className="plan-skin">
          <div className="desk-head mb-7">
            {workspace && (
              <Link to="/w/$slug" params={{ slug }} className="back">
                ← {workspace.name}
              </Link>
            )}
            <div className="crumb">CANVAS</div>
            <div className="krc-head-top">
              <span
                className={`status-pill lg ${status.cls}`}
                title="Derived from how many of this canvas's artifacts are linked to plan work"
              >
                <span className="sp-dot" /> {status.label}
              </span>
            </div>
            <h1 className="page" style={{ margin: "0 0 6px" }}>
              {canvas.name}
            </h1>
            {description && <p className="krc-subtitle">{description}</p>}
            <div className="canvas-doc-kicker">
              <FileText size={13} /> {pageCount} {pageCount === 1 ? "page" : "pages"} · edited{" "}
              {updatedRel}
              {artifactCount > 0 && (
                <>
                  {" "}
                  · {artifactCount} artifact{artifactCount === 1 ? "" : "s"} · {taggedCount} tagged
                </>
              )}
              {revisionId && <> · v{revisionId}</>}
            </div>
          </div>
        </div>

        {/* The canvas sheet + a SIBLING comments overlay. The overlay is a pure
            additive layer: it READS block anchors ([data-block-key]) from the
            CanvasViewer DOM and floats a gutter of comment markers in the right
            margin. It never modifies the canvas markup or editing behavior. The
            relative wrapper is the positioning origin for the absolute gutter. */}
        <div className="canvas-with-comments relative">
          {/* data-tour anchor: the demo guided tour spotlights the canvas
              sheet (the block surface) when it walks onto a canvas page. */}
          <article
            data-tour="canvas-page"
            className={`canvas-page py-12 sm:py-16 ${isLandscape ? "px-6 sm:px-10" : "px-10 sm:px-20"}`}
          >
            <CanvasViewer
              payload={payloadQuery.data}
              isAdmin={mayEdit}
              onVariableChange={async (key, value) => {
                const response = await setVariable.mutateAsync({ key, value });
                return {
                  affected_bindings: response.affected_bindings,
                  affected_blocks: response.affected_blocks,
                };
              }}
            />
          </article>

          {/* Comments overlay — gutter column tucked INTO the sheet's right
              padding (the sheet reserves px-10/sm:px-20 of empty right padding,
              so the 28px gutter sits over blank margin, never over text). We
              deliberately do NOT float it outside the sheet: the canvas lives in
              a resizable left panel, and when the chat rail is open that panel is
              narrow enough that an outside gutter overflows onto the divider/rail
              (the old `translate-x-[calc(100%+14px)]` bug). Pinning 8px in from
              the sheet's right edge keeps markers visible at any panel width.
              Wrapped so the gutter's measured-top math shares the article's top
              edge (markers line up with their blocks). */}
          <div className="pointer-events-none absolute inset-y-0 right-[8px]">
            <CanvasCommentsOverlay canvasId={canvasId} workspaceId={workspace?.id ?? ""} />
          </div>
        </div>

        {/* Version history — on the desk, under the sheet. The canvas now
            changes on its own (the agent's Responses page, an app's page), so
            "what changed while I was away, and put it back" has to be one
            glance and one click rather than a support question. Editors only:
            Restore is a write. */}
        {mayEdit && <CanvasHistory canvasId={canvasId} />}

        {/* Artifacts — around the canvas, not inside it. */}
        <div className="mt-8">
          <CanvasArtifacts
            canvasId={canvasId}
            workspaceId={workspace?.id ?? ""}
            workspaceSlug={slug}
            canEdit={mayEdit}
          />
        </div>
      </div>
    </ScrollArea>
  );
}

/** Canvas status — derived from how much of its artifact output is tagged to
 * plan work (mockup CanvasWork.canvasStatus). Never typed. */
function deriveCanvasStatus(
  artifactCount: number,
  taggedCount: number,
): { label: string; cls: string } {
  if (artifactCount === 0) return { label: "Draft", cls: "neutral" };
  const pct = Math.round((taggedCount / artifactCount) * 100);
  if (pct >= 67) return { label: "On Track", cls: "on" };
  if (pct >= 34) return { label: "At Risk", cls: "risk" };
  return { label: "Off Track", cls: "off" };
}

/** Static one-line description for the canvas header — the canvas table has no
 * description column yet, so derive a readable phrase from the slug, else a
 * generic line keyed off the name. */
function canvasDescription(canvas: { name: string; slug: string }): string {
  const fromSlug = canvas.slug
    .replace(/[-_]+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
  // Compare with punctuation normalized away: a slug that is merely the
  // slugified name ("ic-paper---acme…" vs "IC Paper — Acme…") is an echo,
  // not a description — showing it reads as a lowercase glitch.
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  if (fromSlug && norm(fromSlug) !== norm(canvas.name)) return fromSlug;
  return `Working canvas for ${canvas.name.toLowerCase()}.`;
}
